const { detect } = require('detect-browser');
const browser = detect();
import storage from './SafeStorage';
// import RulesManager from './rulesManager';
import { WebchatEventEmmitter } from './WebchatEventEmmitter';
import { WebchatServer } from './WebchatServer';
import { PreferenceSettings, WebchatUser, WebchatSettingsManager, WebchatSettings, WebchatEmbedEvent, ProfileLangs, EventMessageDetails } from './WebchatSettings';
import { addSeconds, getTime } from 'date-fns';
import { config } from '@/config';
import { WebchatTactfulEvents, WebchatTactfulEventsNames } from './bus-events';
import bugsnagClient from "@/utilities/bugsnag";

export interface TactfulAttachments {
  id: string;
  type: string;
  title: string;
  fileSize: number;
  url: string;
  previewURL: string;
  contentLength?: number;
  originalURL: string;
  coordinatesLat?: number;
  coordinatesLong?: number;
  stickerId?: number;
  element?: {
    [key: string]: any;
  };
}
export interface WebchatMessage {
  type: string;
  text?: string;
  user?: string;
  dataFormat?: string;
  channel?: string;
  timestamp?: number,
  currentSession?: string;
  quick_reply?: any;
  postback?: any;
  user_profile?: WebchatUser;
  profileId?: string;
  brandChannelId?: string;
  device?: string;
  format?: string;
  eventProperties?: { [key: string]: any };
  tactfulAttachments?: TactfulAttachments[];
}

// Handles all the interaction with the webchat server
// and the host website (where webchat is embedded)
export class WebchatClient extends WebchatEventEmmitter {
  server: WebchatServer = new WebchatServer();
  // if initialied, do not attempt re-connections
  initialized: boolean = false;
  isTokenVerified: boolean = true;
  validation_output: any[];
  isCustomerCaptured: boolean = false;  //to ensure customer data is captured EXACTLY once at beginning of convo
  parent_window: Window = null;
  vm: Vue = null;
  settings: PreferenceSettings = new PreferenceSettings();
  webchatSettings: WebchatSettings = new WebchatSettings();
  timer: any = null;
  env: "local" | "qa" | "alpha" | "prod" = "prod";

  constructor(vm: Vue) {
    super();
    this.vm = vm;
    this.on("connected", () => this.onConnect());

    if (window.origin.includes(".qa.")) this.env = "qa";
    else if (window.origin.includes(".alpha.")) this.env = "alpha";
    else if (window.origin.includes("localhost")) this.env = 'local';
    else this.env = "prod";
    this.computeServiceURLS();
  }

  getWebsocketUrl() {
    console.log("this.settings in getWebsocketUrl", this.settings);
    const locationHost = location.host.includes(':8083') ? 'localhost:3000' : location.host;//to allow the websocket work in dev mode using port 8083
    return (location.protocol === 'https:' ? 'wss' : 'ws') + '://' + locationHost + '?profileId=' + this.settings.profileId + '&channelId=' + this.settings.channelId + '&token=' + this.settings.token + '&guid=' + this.settings.guid + '&host_website=' + this.settings.currentSession.url;
  }

  sendMessage(msgContent: WebchatMessage) {
    try {
      if (!msgContent.text && msgContent.tactfulAttachments && msgContent.tactfulAttachments.length == 0) {
        return;
      }
      let deliverMessage = {
        type: msgContent.type || 'message',
        text: msgContent.text,
        user: this.settings.guid,
        dataFormat: msgContent.format,
        channel: 'socket',
        timestamp: msgContent.timestamp,
        currentSession: this.settings.session ? this.settings.session : null,
        eventProperties: msgContent.eventProperties ? msgContent.eventProperties : null,
      } as WebchatMessage;
      if (msgContent.quick_reply)
        deliverMessage.quick_reply = msgContent.quick_reply;
      if (msgContent.postback)
        deliverMessage.postback = msgContent.postback;
      if (msgContent.tactfulAttachments && msgContent.tactfulAttachments.length > 0)
        deliverMessage.tactfulAttachments = msgContent.tactfulAttachments;

      console.log("sendMessage :::::::::::::::: ", deliverMessage);
      this.server.sendMessage(deliverMessage);
    } catch (error) {
      console.error("sendMessage()", error);
      bugsnagClient.notify(error as any);
    }
  }
  async getHistory() {
    if (this.settings.guid) {
      try {
        await this.checkOnTactfulToken();
        let history = await this.server.requestWithHeaders(`/web/history?user=${this.settings.guid}&profileId=${this.settings.profileId}&channelId=${this.settings.channelId}&token=${this.settings.token}&isTokenVerified=${this.isTokenVerified}`, {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${storage.get('tactfulToken')}`
        });
        if (history.success) {
          this.emit('history_loaded', history.history);
        } else {
          this.emit('history_error', new Error(history.error));
        }
      } catch (err) {
        console.error('error in history');
        this.emit('history_error', err);
        bugsnagClient.notify(err as any);
      }
    }
  }

  async getProfileSettings() {
    try {
      await this.checkOnTactfulToken();
      let settings = await this.server.requestWithHeaders(`/web/settings?profileId=${this.settings.profileId}&channelId=${this.settings.channelId}`, {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${storage.get('tactfulToken')}`
      });
      if (settings) {
        console.log("settings success : ", settings);
        this.emit('settings_loaded', settings);
      } else {
        this.emit('settings_error', new Error(settings.error));
      }
    } catch (err) {
      this.emit('settings_error', err);
      bugsnagClient.notify(err as any);
    }
  }

  computeServiceURLS() {
    if (this.env === 'local') return;
    const subDomain = this.env === 'prod' ? '' : `.${this.env}.dev`;
    config.VUE_APP_ATTACHMENTS_MICRO_HOST_URL = `https://attachments${subDomain}.tactful.ai/attachments/v1`;
    config.VUE_APP_ERP_API_HOST_URL = `https://api${subDomain}.tactful.ai/erp/v1`;
  }

  async connect() {
    try {
      await this.getChannelId();
      if(this.settings.enable){
        WebchatSettingsManager.loadUser(this.settings);
        // skip connection if already initialized
        if (this.initialized) {
          console.log("Alreay intialied, skipping connection..", this.settings);
          return;
        }
        await this.checkOnUserToken();
        await this.upsertCustomer();
        this.server.connectWebsocket(this.getWebsocketUrl(), this);
        // mark as initialied to avoid reconnecting or duplicating sockets
        this.initialized = true;
        this.emit('setup_success', true);
      }
    } catch (err) {
      this.emit('setup_error', err);
      bugsnagClient.notify(err as any);
    }
  }

  async onConnect() {
    let connectEvent = 'hello';
    if (WebchatSettingsManager.loadUser(this.settings)) {
      connectEvent = 'welcome_back';
    }
    await this.getProfileLanguageSettings();
    this.settings.enableUserChooseLanguage = false; // ProfileLanguageSettings.enable_user_choose_language 
    this.emit('settings_loaded', { enableUserChooseLanguage: this.settings.enableUserChooseLanguage });
    await this.getProfileWebchatSettings();
    await this.getCustomerPreferredLang();

    const fetchSupportedLanguages = this.settings.enableUserChooseLanguage; // TEMPORARILY, only depend on automation skills to choose language
    if (fetchSupportedLanguages) await this.getProfileSupportedLanguages();
    if (this.checkLoadCaptureLead()) await this.getProfileLangs();

    await this.loadPreferredLangSelector();
    await this.loadWebviewCaptureLead();
    // await this.getAgents(); add if msg src is agent or bot in msg
    await this.getHistory();

    this.vm.$emit('data_loaded');
    // send event connected to embed.js
    this.notifyParent({
      name: 'connected',
      webchatSettings: this.webchatSettings,
      settings: this.settings
    });

    this.sendTactfulEvent(WebchatTactfulEvents.WidgetLoaded);

    // Send the hello or welcome back messages .. this is the first message to be sent
    this.server.sendMessage({
      type: connectEvent,
      user: this.settings.guid,
      channel: 'socket',
      user_profile: this.settings.user,
      profileId: this.settings.profileId,
      device: this.setUserDevice()
    });

    if (this.settings.searchParams?.text) {
      this.vm.$emit('init_message', this.settings.searchParams.text);
    }

    setTimeout(() => {
      if (this.settings.action) {
        this.triggerAction(this.settings.action, this.settings.actionPayload);
      }
      else { // TO DO .. enabel open widget by PROFILE  not all

      }
    }, 3000);

  }
  setUserDevice() {
    let deviceDetails = browser.name + ' ' + browser.type;
    return deviceDetails;
  }

  setUser(user: WebchatUser) {  //Creates or updates this.settings.user, based on its initial value.
    if (!this.settings.user) {
      this.settings.user = user;
    }
    else {
      for (const k in user) {
        if (user[k] != undefined || user[k] !== '') {   //catch both null and undefined or empty string
          this.settings.user[k] = user[k];
        }
      }
    }
  }

  async setUserIdentity(user: WebchatUser, action: string, actionPayload: any) {
    if (!this.isCustomerCaptured) {
      let payload = action ? action : 'RESTART';
      let payloadObj = actionPayload ? actionPayload : {};
      this.setUser(user);
      this.isCustomerCaptured = true;
      storage.set('capturedCustomer', 'true');
      this.sendUserIdentity(user);
      this.sendTactfulEvent(WebchatTactfulEvents.LeadCaptured);
      await this.loadWebviewCaptureLead();
      // this.triggerAction(payload, payloadObj); //Comment Autogreet

    }
  }

  sendUserIdentity(user: WebchatUser) {
    if (this.settings.user) {
      this.server.sendMessage({
        type: 'identify',
        user: this.settings.guid,
        channel: 'socket',
        user_profile: this.settings.user,
        profileId: this.settings.profileId,
        brandChannelId: this.settings.channelId
      });
    }
  }

  async setUserPreferredLanguage(user: WebchatUser) {
    this.setUser(user);
    storage.set('preferredLangSet', 'true');

    await this.getProfileLangs();
    //Comment Autogreet
    // if (!this.checkLoadCaptureLead()) {  // trigger restart only if no capture lead is coming
    //   setTimeout(() => {
    //     this.triggerAction('RESTART', {});
    //   }, 500);
    // }
    this.server.sendMessage({
      type: 'identify',
      user: this.settings.guid,
      channel: 'socket',
      user_profile: this.settings.user,
      profileId: this.settings.profileId,
      brandChannelId: this.settings.channelId
    });
  }

  async validateSnippetParams(event: WebchatEmbedEvent) {
    clearInterval(this.timer);
    if (event.initialized !== undefined) {
      this.initialized = event.initialized;
    }
    this.validation_output = WebchatSettingsManager.areParamsRequired(event.settings); //check if mandatory params have been sent in Tactful.start()

    if (this.validation_output[0]) {  //required params have been received
      this.validation_output = WebchatSettingsManager.areParamsValid(event.settings); //check if available params are of correct type and no html is embedded

      if (this.validation_output[0]) {   //recieved params are valid
        WebchatSettingsManager.updateSettings(this.settings, event.settings);
        console.log("settings are >>> ", this.settings);
        await this.connect();
      } else {  //recieved params aren't valid
        console.error(this.validation_output[1]);
      }
    } else {  //not all required params have been recieved
      console.error(this.validation_output[1]);
    }
  }

  /**
   * Handler for events received from embedding script in parent window.
   * 
   * Events are passed using [postMessage()](https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage)
   * 
   * Refer to function `trigger()` in `embed.ts` to find which events are fired.
   * @param event 
   */
  async onHostCommand(event: WebchatEmbedEvent) {
    switch (event.name) {
      case 'identifyUser':
        // link this account info to this user
        console.log('IDENTIFY', event.user);
        this.setUserIdentity(event.user, null, null);
        break;
      case 'event':
        console.log('in client track ', event.eventDetails);
        this.handleEventMessage(event.eventDetails);
        break;
      case 'configure':
        // link this account info to this user
        console.log('CONFIGURE', event.settings);
        WebchatSettingsManager.updateSettings(this.settings, event.settings);
        break;
      case 'logout':
        console.log('LOGOUT', event);
        WebchatSettingsManager.deleteUser(this.settings.guid as string);
        storage.clear();
        await this.validateSnippetParams(event);
        this.isCustomerCaptured = false;
        await this.onConnect();
        break;
      case 'connect':
        console.log('CONNECT', event);
        await this.validateSnippetParams(event);
        break;
      case 'fullScreen':
        this.toggleView(event.activateFullScreen);
        break;
      case 'mobileTheme':
        this.vm.$emit('activateMobileTheme', event.activateMobileTheme);
        break;
      case 'standaloneMode':
        this.vm.$emit('activateStandaloneMode', event.activateStandaloneMode, event.closeButton);
        break;
      case 'webchatSettings':
        this.vm.$emit('webchat_settings_loaded', event.webchatSettings);
        break;
      case 'triggerAction':
        this.triggerAction(event.actionName, event.actionPayload);
        break;
      case 'fireBubbleTrigger':
        if (event.bubbleActive) {
          this.sendTactfulEvent(WebchatTactfulEvents.WidgetOpened);
        } else {
          this.sendTactfulEvent(WebchatTactfulEvents.WidgetClosed);
        }
        this.vm.$emit('onToggleBubble', event.bubbleActive);
        break;
      default:
        console.log('UNKNOWN COMMAND', event);
    }
  }
  toggleView(activateFullScreen: boolean) {
    this.vm.$emit('isFullScreenMode', activateFullScreen);
  }

  notifyParent(event: WebchatEmbedEvent) {
    if (this.parent_window) {
      this.parent_window.postMessage(event, '*');
    }
  }
  registerVueEventListeners() {
    let that = this;
    let vm = this.vm;
    /**
     * this function used to add listeners to webchat using on function
     * and this function take 2 params (event name ,callback function)
     * note: callback function emit to webChat.vue use ( vm.$emit )
     */

    // add listen when connection stablished
    that.on('connected', async function () {
      vm.$emit('connected', that.settings); // emit to webChat.vue
      console.log("Webchat window initial preference", that.settings.closeWebchat);
      if (!that.settings.closeWebchat) {
        that.notifyParent({ name: "openIframe" });
      }
    });

    // add listen when disconnected
    that.on('disconnected', function () {
      console.log("disconnected : ________________");
      vm.$emit('disconnected');
    });

    // add listen when sent message to bot
    that.on('sent', function () {
      vm.$emit('sent');
    });

    // add listen when sent message to bot
    that.on('settings_loaded', function (settings: any) {
      vm.$emit('settings_loaded', settings);
    });

    // add listen when receive message from bot
    that.on('message', (message: any) => {
      vm.$emit('message', message);
      if (that.settings.closeWebchat) {
        // that.notifyParent({ name: "openIframe" });
        this.notifyParent({
          name: 'showLastMessage',
          lastMessage: message.text
        });
      }
    });

    that.on('metadata', (e) => {
      if (e.sender_action == 'typing_on') {
        vm.$emit('typing', true);
      } else if (e.sender_action == 'typing_off') {
        vm.$emit('typing', false);
      }
    });

    that.on('history_loaded', function (history: any) {
      if (history) {
        vm.$emit('history_loaded', history);
      } else {
        vm.$emit('empty_history', history);
      }
    });
    that.on('webchat_settings_loaded', function (webchatSettings: WebchatSettings) {
      vm.$emit('webchat_settings_loaded', webchatSettings);
    });
    that.on('webchat_settings_error', function (error: Error) {
      vm.$emit('webchat_settings_error', error);
    });
    that.on('supported_languages_loaded', function ({ data, error }: { data: any; error: Error }) {
      vm.$emit('supported_languages_loaded', { data, error });
    });
    that.on('language_settings_loaded', function ({ data, error }: { data: any; error: Error }) {
      vm.$emit('language_settings_loaded', { data, error });
    });
    that.on('agents_loaded', function (agents: any) {
      if (agents) {
        vm.$emit('agents_loaded', agents);
      }
    });
    that.on('langs_loaded', function (langs: ProfileLangs) {
      vm.$emit('langs_loaded', langs);
    });
    that.on('langs_error', function (err: Error) {
      vm.$emit('langs_error', err);
    });
    that.on('loadUserLanguageSelector', function (loadUserLangSelector: boolean) {
      vm.$emit('loadUserLanguageSelector', loadUserLangSelector);
    });
    that.on('enableWebview', function (loadWebview: boolean) {
      vm.$emit('enableWebview', loadWebview);
    });
    that.on('lang_updated', function (lang: string) {
      vm.$emit('lang_updated', lang);
    });
    that.on('setup_error', function (err: Error) {
      vm.$emit('setup_error', err);
    });
    that.on('setup_success', function () {
      vm.$emit('setup_success');
    });
    that.on('data_loaded', function () {
      vm.$emit('data_loaded');
    });
  }

  // if an action is specified in webchat query string, trigger it
  triggerAction(action: string, actionPayload: any) {
    let that = this;
    let payload = { 'PAYLOAD': action, ...actionPayload };
    let text = action.toUpperCase();
    let format = 'text';
    if (that.settings.voiceMode) {
      format = "voice";
    }

    let msg = {
      format: format,
      text: text,
      type: 'action_trigger',
      postback: {
        title: '',
        payload: JSON.stringify(payload)
      },
    } as WebchatMessage;
    this.sendMessage(msg);
  }

  start() {
    console.log('Booting up');

    this.registerVueEventListeners();
    if (window.self !== window.top) {
      // this is embedded in an iframe.
      // send a message to the master frame to tell it that the chat client is ready
      // do NOT automatically connect... rather wait for the connect command.
      this.parent_window = window.parent;
      window.addEventListener("message", (ev: MessageEvent) => { this.onHostCommand(ev.data); }, false);
      console.log("ready to recieve events");
      this.timer = setInterval(() => {
        this.notifyParent({
          name: 'booted'
        });
      }, 1000);

      if (window.location.href?.includes('?type=back'))
        this.notifyParent({
          name: 'openIframe',
        });

      console.log('Webchat booted in embedded mode');
    } else {
      console.log('Webchat booted in stand-alone mode');
      // this is a stand-alone client. connect immediately.
      // url example : "http://localhost:8081/chatChannel.html#/?profile=tactfulkids&voice=enable&language=arabic"
      let url;
      try {
        url = new URL(window.location.href);
      } catch {
        url = null;
      }
      if (url) {
        WebchatSettingsManager.parseUrlParams(this.settings, url);
        this.settings.currentSession = { url: url.origin + url.pathname, title: '' };
        this.connect();
      }
    }
    return this;
  }

  async getProfileWebchatSettings() {
    try {
      await this.checkOnTactfulToken();
      let webchatSettings: WebchatSettings = await this.server.requestWithHeaders(`/web/webchat_settings?profileId=${this.settings.profileId}&channelId=${this.settings.channelId}&token=${this.settings.token}`, {
        "Content-Type": "application/json",
        "Authorization": 'Bearer' + " " + storage.get('tactfulToken')
      });
      this.webchatSettings = webchatSettings;
      if (webchatSettings) {
        this.emit('webchat_settings_loaded', webchatSettings);
      } else {
        this.emit('webchat_settings_error', new Error());
      }
    } catch (err) {
      this.emit('webchat_settings_error', err);
      bugsnagClient.notify(err as any);
    }
  }

  async getProfileSupportedLanguages() {
    try {
      await this.checkOnTactfulToken();
      const supportedLanguages: { profile: any; languages: any[] } = await this.server.requestWithHeaders(`/web/supported_languages?profileId=${this.settings.profileId}`, {
        "Content-Type": "application/json",
        "Authorization": 'Bearer' + " " + storage.get('tactfulToken')
      });
      this.emit('supported_languages_loaded', { data: supportedLanguages, error: null });
    } catch (err) {
      this.emit('supported_languages_loaded', { data: null, error: err });
      bugsnagClient.notify(err as any);
    }
  }

  async getProfileLanguageSettings() {
    try {
      await this.checkOnTactfulToken();
      const languageSettings: any = await this.server.requestWithHeaders(`/web/profile_language_settings?profileId=${this.settings.profileId}`, {
        "Content-Type": "application/json",
        "Authorization": 'Bearer' + " " + storage.get('tactfulToken')
      });
      this.emit('language_settings_loaded', { data: languageSettings, error: null });
      return languageSettings;
    } catch (err) {
      this.emit('language_settings_loaded', { data: null, error: err });
      bugsnagClient.notify(err as any);
    }
  }

  async getProfileLangs() {
    try {
      await this.checkOnTactfulToken();
      let apiURL = `/web/langs?profileId=${this.settings.profileId}&channelId=${this.settings.channelId}&token=${this.settings.token}`;
      if (this.settings.user && this.settings.user.preferred_language) {
        apiURL = apiURL + `&userPreferredLang=${this.settings.user.preferred_language}`;
      }

      let langs: ProfileLangs = await this.server.requestWithHeaders(apiURL, {
        "Content-Type": "application/json",
        "Authorization": 'Bearer' + " " + storage.get('tactfulToken')
      });
      if (langs) {
        this.emit('langs_loaded', langs);
        console.log("langs success : ", langs);
      } else {
        this.emit('langs_error', new Error());
      }
    } catch (err) {
      this.emit('langs_error', err);
      bugsnagClient.notify(err as any);
    }
  }

  async getCustomerPreferredLang() {
    try {
      await this.checkOnTactfulToken();
      let response = await this.server.requestWithHeaders(`/web/customer_preferred_language?profileId=${this.settings.profileId}&channelId=${this.settings.channelId}&token=${this.settings.token}&guid=${this.settings.guid}`, {
        "Content-Type": "application/json",
        "Authorization": 'Bearer' + " " + storage.get('tactfulToken')
      });
      if (response.preferred_language) {
        this.emit('lang_updated', response.preferred_language);
      } else {
        console.log("In getCustomerPreferredLang ... customer has no preferred language");
      }
    } catch (err) {
      console.error(err);
      bugsnagClient.notify(err as any);
    }
  }

  checkLoadLangSelector() {
    if (!this.settings.enableUserChooseLanguage) return false;
    return (!this.settings.user || !this.settings.user.preferred_language) && (!storage.get('preferredLangSet') || storage.get('preferredLangSet') == 'false');
  }

  async loadPreferredLangSelector() {
    await this.checkOnTactfulToken();
    const loadUserLangSelector = this.checkLoadLangSelector();
    if (loadUserLangSelector) {
      this.emit('loadUserLanguageSelector', true);
    } else {
      this.emit('loadUserLanguageSelector', false);
    }
    return loadUserLangSelector;
  }

  checkLoadCaptureLead() {
    let enableWebview = this.webchatSettings.enableWebviewCaptureLead;
    return enableWebview && (!storage.get('capturedCustomer') || storage.get('capturedCustomer') == 'false');
  }

  async loadWebviewCaptureLead() {
    await this.checkOnTactfulToken();
    if (this.checkLoadCaptureLead()) {
      this.emit('enableWebview', true);
    }
    else { this.emit('enableWebview', false); }
  }

  generateTimestamp(expires_in: number) {
    const expired = (0.9 * expires_in);
    const currentDate = new Date();
    const futureDate = addSeconds(currentDate, expired);
    const timestampMillis = Math.floor(getTime(futureDate));
    return timestampMillis;
  }
  async generateTactfulToken() {
    this.settings.profile = 'superdemo';
    const { tactfulToken, customer_id } = await this.server.request(`/web/generate_tactful_token?profileId=${this.settings.profileId}&channelId=${this.settings.channelId}&token=${this.settings.token}&guid=${this.settings.guid}&isVerified=${this.isTokenVerified}`);

    if (tactfulToken?.access_token && tactfulToken.expires_in && customer_id) {
      const timestampMillis = this.generateTimestamp(tactfulToken.expires_in);
      storage.set('tactfulToken', tactfulToken.access_token);
      storage.set('expires_in', `${timestampMillis}`);
      storage.set('customerId', `${customer_id}`);
    }
    else {
      console.log('Token not generated !!!!');
    }
  }

  async upsertCustomer() {
    await this.checkOnTactfulToken();

    const customerPayload: any = this.settings.user ?? {};
    customerPayload.webchat_id = this.settings.guid;

    const body = { customerPayload, profileId: this.settings.profileId, channelId: this.settings.channelId };
    const customer = await this.server.requestWithBodyAndHeaders("/web/customer", {
      "Content-Type": "application/json",
      "Authorization": 'Bearer' + " " + storage.get('tactfulToken')
    }, body, "PUT");
    this.settings.user = customer;
    this.settings.guid = customer.webchat_id;
  }

  async generateUserToken() {
    this.settings.profile = 'superdemo';
    const userToken = await this.server.request(`/web/generate_user_token?profileId=${this.settings.profileId}&channelId=${this.settings.channelId}&token=${this.settings.token}&guid=${this.settings.guid}&isVerified=${this.isTokenVerified}`);
    if (userToken.user_token) {
      try {
        storage.set('userToken', userToken.user_token);
      }
      catch (err) {
        console.warn("Anonymous mode");
      }
    }
  }
  async validateUserToken(userToken: string, _customer: any) {
    const validateUserToken = await this.server.request(`/web/validate_user_token?profileId=${this.settings.profileId}&channelId=${this.settings.channelId}&token=${this.settings.token}&userToken=${userToken}`);
    let channelIdInToken: string = null;
    console.log("validateUserToken :: ", validateUserToken);
    if (validateUserToken.authenticated) {
      channelIdInToken = validateUserToken.data?.channelId;
      console.log("token in validation :: ", validateUserToken?.data?.new_token);
      if ((channelIdInToken != this.settings.channelId)) {
        console.log("channelIdInToken :: ", this.settings.channelId);
        this.settings.guid = WebchatSettingsManager.generate_guid();
        if (storage.get('capturedCustomer')) {
          storage.set('capturedCustomer', 'false');
          storage.set('preferredLangSet', 'false');
        }
        if (storage.get('user_info_saved')) {
          storage.set('user_info_saved', 'false');
        }
      } else {
        this.settings.guid = validateUserToken.data.guid;
        this.isTokenVerified = validateUserToken.data.isVerified;
      }
    }
    return validateUserToken;
  }

  async checkOnUserToken() {
    let userToken = null;
    let validateUserToken: any;
    try {
      userToken = storage.get('userToken');
    } catch (err) {
      console.warn("we seem to be running incognito, user will be anonymous", err);
    }
    if (userToken) {
      validateUserToken = await this.validateUserToken(userToken, {});
      if (validateUserToken.authenticated && validateUserToken.data?.channelId && validateUserToken.data?.guid) {
        this.settings.guid = validateUserToken.data.guid;  //update guid with validated guid to get history
      }
    }
    const tokenNotExist = !userToken;
    const userNotAuthenticated = !validateUserToken?.authenticated || (validateUserToken?.data && !validateUserToken.data.isVerified);
    const newChannel = validateUserToken && validateUserToken?.data?.channelId != this.settings.channelId;
    if (newChannel) {
      this.settings.guid = WebchatSettingsManager.generate_guid(); // must generate new guid and new token to new channel
      this.settings.isReturningUser = false;
      await this.generateUserToken();
      await this.generateTactfulToken();
    }

    if (tokenNotExist || userNotAuthenticated) {
      console.log("generate new one...");
      //generate guid if not exist
      if (!this.settings.guid || this.settings.guid == 'null' || this.settings.guid == 'undefined') {
        this.settings.guid = WebchatSettingsManager.generate_guid();
        this.settings.isReturningUser = false;
      }

      await this.generateUserToken();
      await this.generateTactfulToken();
    }
    storage.set('tactful_guid', `${this.settings.guid}`);
  }

  checkIfExpiredToken(timestamp: number) {
    const currentDate = new Date();
    let now = Math.floor(getTime(currentDate));
    let expiredToken = (timestamp <= now) ? true : false;
    return expiredToken;
  }

  async checkOnTactfulToken() {
    let tactfulTokenExpiry = null;
    try {
      tactfulTokenExpiry = storage.get('expires_in');
    } catch (err) {
      console.warn("we seem to be running incognito, user will be anonymous", err);
      return;
    }
    if (tactfulTokenExpiry) {
      // will be endpoint for check token validation later
      const timestampExpiry = this.checkIfExpiredToken(parseInt(tactfulTokenExpiry));
      if (timestampExpiry) {
        console.log("generate new one...");
        await this.generateTactfulToken();
        console.log('In checkOnTactfulToken, expiry, this.settings.guid', this.settings.guid);
      }
    }
    const tokenNotExist = !tactfulTokenExpiry;
    console.log("tactfulValidation is :: ", tokenNotExist);
    if (tokenNotExist && this.settings.guid) {
      console.log("generate new one...");
      await this.generateTactfulToken();
      console.log('In checkOnTactfulToken, tokenNotExist, this.settings.guid', this.settings.guid);
    }
    else {
      console.log("UnExcepted behavior...");
    }
  }

  handleEventMessage(event: EventMessageDetails) {
    let msg: WebchatMessage = {
      text: (event && event.name) ? event.name : 'webEventMessage',
      type: 'event_message',
      timestamp: Date.now(),
      profileId: this.settings.profileId,
      user: this.settings.guid,
      eventProperties: (event && event.properties) ? event.properties : {},
    };
    this.sendMessage(msg);
  }
    async getChannelId() {
    this.settings.enable = false;
    let result = await this.server.requestWithHeaders(`/web/channels?profileId=${this.settings.profileId}&token=${this.settings.token}`, {
      "Content-Type": "application/json",
      //  "Authorization": 'Bearer' + " " + storage.get('tactfulToken')
    });
    if(result) {
      this.settings.channelId = result.channelId && result.channelId.toString();
      this.settings.enable = result.enable ? true : false;
    }
  }
  async uploadtAttachment(body: any) {
    try {
      await this.checkOnTactfulToken();
      const headers = { "X-API-KEY": 'Bearer' + " " + storage.get('tactfulToken') };
      const attachment = await this.server.coreApiRequest(`${config.VUE_APP_ATTACHMENTS_MICRO_HOST_URL}/attachments/uploadAttachment`, "POST", headers, body);

      if (attachment.status == 200) {
        return attachment.json();
      }
    } catch (error) {
      console.log(error);
      this.emit('attachment_error', error);
      bugsnagClient.notify(error as any);
    }
  }

  async getCountries() {
    try {
      await this.checkOnTactfulToken();
      const headers = { "X-API-KEY": 'Bearer' + " " + storage.get('tactfulToken') };
      const res = await this.server.coreApiRequest(`${config.VUE_APP_ERP_API_HOST_URL}/countries`, "GET", headers);

      console.log(res, "countries");

      if (res.status == 200) {
        return res.json();
      }
    } catch (error) {
      console.log(error);
      this.emit('countries_error', error);
      bugsnagClient.notify(error as any);
    }
  }

  async getAttachment(attachment_id: any) {
    try {
      await this.checkOnTactfulToken();
      const headers = { "X-API-KEY": 'Bearer' + " " + storage.get('tactfulToken') };
      let attachment = await this.server.coreApiRequest(`${config.VUE_APP_ATTACHMENTS_MICRO_HOST_URL}/attachments/${attachment_id}`, "GET", headers);
      if (attachment.status == 200) {
        return attachment.json();
      }
    }
    catch (error) {
      this.emit('attachment_error', error);
      bugsnagClient.notify(error as any);
    }
  }
  async deleteAttachment(attachment_id: any) {
    try {
      await this.checkOnTactfulToken();
      const headers = { "X-API-KEY": 'Bearer' + " " + storage.get('tactfulToken') };
      let attachment: any = await this.server.coreApiRequest(`${config.VUE_APP_ATTACHMENTS_MICRO_HOST_URL}/attachments/${attachment_id}`, "DELETE", headers);
      if (attachment.status == 200) {
        return attachment.json();
      }
    } catch (error) {
      this.emit('attachment_error', error);
      bugsnagClient.notify(error as any);
    }
  }

  async getAttachmentSettings() {
    try {
      await this.checkOnTactfulToken();
      console.log("in getAttachmentSettings", config.VUE_APP_ATTACHMENTS_MICRO_HOST_URL);
      const headers = { "X-API-KEY": 'Bearer' + " " + storage.get('tactfulToken') };
      let attachment: any = await this.server.coreApiRequest(`${config.VUE_APP_ATTACHMENTS_MICRO_HOST_URL}/attachments/settings`, "GET", headers);
      const res = await attachment.json();
      if (res.id) {
        return res;
      }
    } catch (err) {
      console.error('attachmentSettingsError: ', err);
      bugsnagClient.notify(err as any);
    }
  }
  async getLinkPreview(url: string) {
    try {
      const result = await this.server.request(`/web/linkPreview?profile=${this.settings.profile}&channelId=${this.settings.channelId}&url=${url}&token=${this.settings.token}`);
      return result;
    } catch (err) {
      console.error("There is an error in getting metadata", err);
      bugsnagClient.notify(err as any);
    }
  }
  closeWebsocket() {
    this.server.closeWebsocket();
  }

  /**
   * Sends an event to Webchat's backend using the WebchatServer instance.
   * 
   * Gathers the payload from WebchatClient's state.
   * 
   * Available events and their payloads are defined in bus-events.ts
   * 
   * @param name Name of the event
   */
  sendTactfulEvent<T extends WebchatTactfulEventsNames>(name: T) {
    let url = "";
    if (window.location)
      url = this.settings.currentSession.url || '';

    let customerId = 0;
    if (storage.get('customerId'))
      customerId = Number(storage.get('customerId'));

    const payload = {
      url,
      customerId,
      conversationId: this.settings.guid,
      channelId: this.settings.channelId,
      profileId: Number(this.settings.profileId),
      captureLeadEnabled: this.webchatSettings.enableWebviewCaptureLead,
      pageTitle: this.settings.currentSession.pageTitle || '',
    };

    this.server.sendEvent(name, payload);
  }

}
